import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useCookieConsent } from "@/hooks/useCookieConsent";

/**
 * LinkedIn Insight Tag.
 *
 * Loaded site-wide (via Layout and LandingLayout) so that every public page —
 * including campaign landing pages — can report conversions and demographics
 * back to LinkedIn Campaign Manager.
 *
 * Consent: the tag sets advertising cookies, so it only loads once the visitor
 * has granted analytics consent. It is never loaded for essential-only visitors.
 *
 * SPA note: LinkedIn records a page view on script load. Because this is a
 * single-page app, we re-fire a lightweight pixel on each route change so
 * downstream URL-based conversion rules still work.
 */
const PARTNER_ID = "9579146";

declare global {
  interface Window {
    _linkedin_partner_id?: string;
    _linkedin_data_partner_ids?: string[];
    lintrk?: ((action: string, payload?: unknown) => void) & { q?: unknown[] };
  }
}

export function LinkedInInsightTag() {
  const { analytics } = useCookieConsent();
  const location = useLocation();
  const loaded = useRef(false);
  const lastPath = useRef<string | null>(null);

  // Inject the tag once, after analytics consent is granted.
  useEffect(() => {
    if (!analytics || loaded.current || typeof window === "undefined") return;
    if (!PARTNER_ID) return;
    loaded.current = true;

    window._linkedin_partner_id = PARTNER_ID;
    window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
    window._linkedin_data_partner_ids.push(PARTNER_ID);

    if (!window.lintrk) {
      const l = function (action: string, payload?: unknown) {
        (l as any).q.push([action, payload]);
      } as NonNullable<Window["lintrk"]>;
      (l as any).q = [];
      window.lintrk = l;
    }

    const script = document.createElement("script");
    script.async = true;
    script.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js";
    document.head.appendChild(script);

    // Non-JS / blocked-script fallback pixel.
    const noscript = document.createElement("noscript");
    const img = document.createElement("img");
    img.height = 1;
    img.width = 1;
    img.style.display = "none";
    img.alt = "";
    img.src = `https://px.ads.linkedin.com/collect/?pid=${PARTNER_ID}&fmt=gif`;
    noscript.appendChild(img);
    document.body.appendChild(noscript);
  }, [analytics]);

  // Re-fire on SPA route changes so page-based conversions still register.
  useEffect(() => {
    if (!analytics || !PARTNER_ID) return;
    const path = location.pathname + location.search;
    if (lastPath.current === null) {
      lastPath.current = path; // initial load is covered by the tag itself
      return;
    }
    if (lastPath.current === path) return;
    lastPath.current = path;

    const img = new Image(1, 1);
    img.src = `https://px.ads.linkedin.com/collect/?pid=${PARTNER_ID}&fmt=gif&time=${Date.now()}`;
  }, [analytics, location.pathname, location.search]);

  return null;
}

/** Fire a LinkedIn conversion from anywhere in the app. */
export function trackLinkedInConversion(conversionId: number) {
  if (typeof window === "undefined" || typeof window.lintrk !== "function") return;
  window.lintrk("track", { conversion_id: conversionId });
}
